﻿import React from "react";
import { useActivitiesData, useActivityItem } from "./ActivitiesDataContext";

/**
 * Builds the activity link URL using either:
 * 1. A URL template with placeholders (if linkUrlTemplate is provided)
 * 2. A simple base + participantId pattern (fallback)
 *
 * Supported template placeholders:
 * - {activityGuid} - The activity's GUID
 * - {userId} - The first participant's userId
 * - {participantId} - The first participant's participantId
 */
function buildActivityHref(
    item: { activityGuid: string; participants: Array<{ participantId: string; userId: number }> },
    linkUrlTemplate: string | null | undefined,
    linkBase: string
): string {
    // If a URL template is provided, use it with placeholder replacement
    if (linkUrlTemplate) {
        const participant = item.participants[0];
        return linkUrlTemplate
            .replace('{activityGuid}', item.activityGuid)
            .replace('{userId}', String(participant?.userId ?? ''))
            .replace('{participantId}', participant?.participantId ?? '');
    }
    // Fallback to simple base + participantId
    return `${linkBase}${item.participants[0]?.participantId ?? ''}`;
}

const ActivityHeader = {
    fields: {
        showViewButton: {
            type: "radio" as const,
            options: [
                { label: "Show", value: true },
                { label: "Hide", value: false },
            ],
        },
        buttonLabel: { type: "text" as const },
        linkBase: { type: "text" as const },
    },
    defaultProps: {
        showViewButton: true,
        buttonLabel: "View",
        linkBase: "/activities/",
    },
    inline: true,
    render: (props: any) => {
        const { helpers, linkUrlTemplate } = useActivitiesData();
        const { item } = useActivityItem();

        // Runtime-safe defaults for legacy data (defaultProps only affect newly added instances in editor)
        const show = props?.showViewButton !== false;
        const label = (props?.buttonLabel ?? "View") as string;
        const base =
            typeof props?.linkBase === "string" && props.linkBase
                ? (props.linkBase as string)
                : "/activities/";
        const href = buildActivityHref(item, linkUrlTemplate, base);

        const name = item.train?.vname ?? `Activity ${item.pulseMonitorActivityId}`;
        const d = item._trainDate ?? null;
        const last = item._lastModified ?? null;

        return (
            <div
                ref={props.puck.dragRef}
                className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                style={{ padding: 'clamp(6px, 1.5vw, 14px)' }}
            >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="text-lg sm:text-xl">❤️</div>
                    <div className="min-w-0">
                        <div className="font-semibold truncate">
                            {helpers.formatHeaderDate(d, last)}
                        </div>
                        <div className="text-xs truncate">{name}</div>
                    </div>
                </div>
                {show && (
                    <a
                        href={href}
                        className="self-start sm:self-auto shrink-0 px-3 py-1.5 text-xs md:text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                        title="View activity details"
                    >
                        {label}
                    </a>
                )}
            </div>
        );
    },
};

export default ActivityHeader;
